import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../context/CreditContext';
import { useCostCalculator } from '../hooks/useCostCalculator';
import { VID_MODELS, RESOLUTIONS } from '../constants/models';
import * as api from '../services/api';
import Icons from '../components/Icons';
import Lightbox from '../components/Lightbox';
import { groupItemsByDate } from '../utils/grouping';
import toast from 'react-hot-toast';
import { compressImage } from '../utils/imageCompression';
import FuturisticSlider from '../components/FuturisticSlider';
import MediaLibraryModal from '../components/MediaLibraryModal';
import { confirmAlert } from '../utils/confirmAlert';
import AspectRatioSelector from '../components/AspectRatioSelector';
import TemplateSlideWindow from '../components/TemplateSlideWindow';

// MAX_TOTAL_DURATION computed per-model inside component

export default function VideoPage({ onShowAuth }) {
  const { user, token } = useAuth();
  const { credits, setCredits, plan, items, refreshItems, removeItem, pendingJobs, setPendingJobs } = useCredits();
  const navigate = useNavigate();
  const location = useLocation();
  const isFrame2Video = location.pathname === '/frame2video';

  const [prompt, setPrompt] = useState('');
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [vidModel, setVidModel] = useState('kling-3');
  const [resolution, setRes] = useState('720p');
  const [duration, setDur] = useState('5s');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [vMode, setVMode] = useState('Standard');
  const [audioOn, setAudioOn] = useState(true);
  const [nsfwChecker, setNsfwChecker] = useState(false);
  const [numVideos, setNumVideos] = useState(1);
  const [loading, setLoading] = useState(false);
  const [genVids, setGenVids] = useState([]); // all generated video URLs
  const [cd, setCd] = useState(0);
  const [showModelPicker, setShowModelPicker] = useState(false);
  const [promptExpansion, setPromptExpansion] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [lbItem, setLbItem] = useState(null);
  const cdRef = useRef();
  const genLockRef = useRef(false);

  // Multi-shot state
  const [multiShotOn, setMultiShotOn] = useState(false);
  const [shots, setShots] = useState([{ prompt: '', duration: 5 }]);

  // Frame-to-video
  const [startFrame, setStartFrame] = useState(null);
  const [endFrame, setEndFrame] = useState(null);
  const sfRef = useRef();
  const efRef = useRef();

  // Element references (Kling 3.0)
  const [refImages, setRefImages] = useState([]);
  const refInputRef = useRef();

  // Seedance reference
  const [seedanceRefs, setSeedanceRefs] = useState([]);
  const [libTarget, setLibTarget] = useState(null); // 'startFrame', 'endFrame', 'refImages', 'seedanceRefs'

  // Auto-suggest state
  const [showSuggest, setShowSuggest] = useState(false);
  const [activeInputIdx, setActiveInputIdx] = useState(null);

  const handleOptimize = async (index = null) => {
    const textToOptimize = index !== null ? shots[index].prompt : prompt;
    if (!textToOptimize.trim()) {
      toast.error('Enter a basic prompt to optimize first.');
      return;
    }
    navigate('/prompt', { state: { prompt: textToOptimize, mode: multiShotOn ? 'multishot' : 'video', modelId: vidModel } });
  };

  const handlePromptInput = (e, index) => {
    const val = e.target.value;
    if (index === null) setPrompt(val);
    else {
      const newShots = [...shots];
      newShots[index].prompt = val;
      setShots(newShots);
    }

    const target = e.target;
    target.style.height = '0px';
    target.style.height = target.scrollHeight + 'px';

    if (vidModel === 'kling-3' && refImages.length > 0) {
      if (val.match(/@[\w]*$/)) {
        setShowSuggest(true);
        setActiveInputIdx(index);
      } else {
        setShowSuggest(false);
      }
    } else if (vidModel.startsWith('seedance-2') && seedanceRefs.length > 0) {
      if (val.match(/@[\w]*$/)) {
        setShowSuggest(true);
        setActiveInputIdx(index);
      } else {
        setShowSuggest(false);
      }
    } else {
      setShowSuggest(false);
    }
  };

  const insertSuggestion = (tagOrIdx) => {
    let insertion = '';
    if (typeof tagOrIdx === 'string') {
      insertion = `${tagOrIdx} `; // seedance tag
    } else {
      insertion = `@image${tagOrIdx + 1} `; // kling tag
    }

    if (activeInputIdx === null) {
      setPrompt(prompt.replace(/@[\w]*$/, insertion));
    } else {
      const newShots = [...shots];
      newShots[activeInputIdx].prompt = newShots[activeInputIdx].prompt.replace(/@[\w]*$/, insertion);
      setShots(newShots);
    }
    setShowSuggest(false);
    setActiveInputIdx(null);
  };

  const curModel = VID_MODELS.find(m => m.id === vidModel) || VID_MODELS[0];
  const MAX_TOTAL_DURATION = vidModel === 'kling-3' ? 15 : 14;
  const canMultiShot = curModel.multiShot;
  const isMultiShot = canMultiShot && multiShotOn;

  const isModelLocked = useCallback((id) => {
    if (id === 'seedance-2' && (!plan || plan === 'free' || plan === 'basic')) {
      return true;
    }
    return false;
  }, [plan]);

  useEffect(() => {
    if (isModelLocked(vidModel)) {
      let fallback;
      if (vidModel === 'seedance-2' && !isModelLocked('seedance-2-fast')) {
        fallback = 'seedance-2-fast';
      } else {
        fallback = VID_MODELS.find(m => !isModelLocked(m.id))?.id || VID_MODELS[0].id;
      }
      setVidModel(fallback);
    }
  }, [plan, vidModel, isModelLocked]);

  // ── Initialization from Router State ──
  const initProcessed = useRef(false);

  useEffect(() => {
    const st = location.state;
    if (!st || initProcessed.current) return;
    initProcessed.current = true;

    let resolvedModel = vidModel;
    // 1. Setup Model
    if (st.modelId) {
      const cleanModel = String(st.modelId).trim().toLowerCase();
      let newModel = st.modelId;
      
      const m = VID_MODELS.find(x => 
        x.id.toLowerCase() === cleanModel || 
        x.label.toLowerCase() === cleanModel ||
        x.id.toLowerCase() === cleanModel.replace(/\s+/g, '-')
      );
      if (m) newModel = m.id;

      resolvedModel = newModel;
      setVidModel(newModel);
      if (m && !m.multiShot) setMultiShotOn(false);
    }

    // 2. Setup text settings (Prompt & Multi-shot toggle)
    if (st.prompt) setPrompt(st.prompt);
    if (st.isMultiShot !== undefined) setMultiShotOn(st.isMultiShot);

    // 3. Setup incoming visual references (if any)
    if (st.action) {
      const setupIncoming = async () => {
        const API_BASE = process.env.REACT_APP_API_URL || '/api';

        // Helper: fetch a single URL -> compressed File
        const fetchRef = async (url, name = 'reference.jpg') => {
          const fetchUrl = url.startsWith('http')
            ? `${API_BASE}/proxy-image?url=${encodeURIComponent(url)}`
            : url;
          const resp = await fetch(fetchUrl);
          if (!resp.ok) throw new Error(`Failed to fetch: ${url}`);
          const blob = await resp.blob();
          const file = new File([blob], name, { type: blob.type || 'image/jpeg' });
          return compressImage(file);
        };

        try {
          if (st.action === 'image-to-video' && st.sourceUrl) {
            const compressed = await fetchRef(st.sourceUrl, 'start-frame.jpg');
            setStartFrame(compressed);

          } else if (st.action === 'use-as-reference') {
            const urls = st.refImageUrls && st.refImageUrls.length > 0
              ? st.refImageUrls
              : st.sourceUrl ? [st.sourceUrl] : [];
            const vidUrls = (Array.isArray(st.refVideoUrl) ? st.refVideoUrl : [st.refVideoUrl]).filter(u => typeof u === 'string' && u.trim());
            const audUrls = (Array.isArray(st.refAudioUrl) ? st.refAudioUrl : [st.refAudioUrl]).filter(u => typeof u === 'string' && u.trim());

            if (urls.length > 0 || vidUrls.length > 0 || audUrls.length > 0) {
              const compressed = urls.length > 0 ? await Promise.all(
                urls.map((url, i) => fetchRef(url, `reference-${i + 1}.jpg`))
              ) : [];
              
              if (resolvedModel.startsWith('seedance-2')) {
                let imgIdx = 1;
                const newRefs = compressed.slice(0, 9).map(file => ({
                  fileType: 'image',
                  file,
                  tag: `@Image${imgIdx++}`
                }));
                let vidIdx = 1;
                vidUrls.slice(0, 3).forEach(v => newRefs.push({ fileType: 'video', fileUrl: v, tag: `@Video${vidIdx++}` }));
                let audIdx = 1;
                audUrls.slice(0, 3).forEach(a => newRefs.push({ fileType: 'audio', fileUrl: a, tag: `@Audio${audIdx++}` }));
                
                setSeedanceRefs(prev => [...prev, ...newRefs].slice(0, 12));
              } else {
                if (compressed.length > 0) {
                  setVidModel(prev => (prev !== 'kling-3' ? 'kling-3' : prev));
                  setRefImages(prev => [...prev, ...compressed].slice(0, 3));
                }
              }
            }
          }
        } catch (e) {
          console.error('Failed to load incoming reference(s):', e);
        }
      };
      setupIncoming();
    }

    // Clear state so reload doesn't trigger it again
    const newState = { ...st };
    delete newState.modelId;
    delete newState.prompt;
    delete newState.action;
    delete newState.sourceUrl;
    delete newState.refImageUrls;
    delete newState.isMultiShot;
    navigate(location.pathname, { replace: true, state: newState });

  }, [location.state, location.pathname, navigate]);

  const handleModelChange = (newModel) => {
    if (isModelLocked(newModel)) {
      setShowModelPicker(false);
      navigate('/subscription');
      return;
    }
    
    setVidModel(newModel);
    const m = VID_MODELS.find(x => x.id === newModel);
    if (m && !m.multiShot) setMultiShotOn(false);
    setShowModelPicker(false);
  };

  // Calculate total shot duration & remaining
  const totalShotDuration = shots.reduce((sum, s) => sum + (s.duration || 0), 0);
  const remainingDuration = MAX_TOTAL_DURATION - totalShotDuration;

  // Legacy multi-shot pricing for older models. Kling 3 uses the shared calculator below.
  const multiShotCost = isMultiShot ? totalShotDuration * 2 : 0;

  const effectiveAudioOn = vidModel === 'kling-2.1' ? false : audioOn;

  const baseCost = useCostCalculator({
    mode: isFrame2Video ? 'frame2video' : 'video',
    model: curModel,
    vidModel, videoMode: vMode, resolution, duration: isMultiShot ? `${totalShotDuration}s` : duration,
    audioOn: effectiveAudioOn, numVideos, shots: isMultiShot ? shots : undefined,
    hasVideoInput: seedanceRefs.some(r => r.fileType === 'video'),
  });

  const cost = (isMultiShot && vidModel !== 'kling-3' && vidModel !== 'wan-2.7') ? multiShotCost * Math.min(numVideos || 1, 4) : baseCost;
  const shotCreditBreakdown = useMemo(() => {
    if (!isMultiShot || totalShotDuration <= 0 || !Number.isFinite(cost)) {
      return shots.map(() => 0);
    }

    const totalCost = Math.max(0, Math.round(cost));
    const rawCosts = shots.map(s => ((s.duration || 0) / totalShotDuration) * totalCost);
    const roundedCosts = rawCosts.map(v => Math.floor(v));
    let remainder = totalCost - roundedCosts.reduce((sum, v) => sum + v, 0);

    return roundedCosts.map(v => {
      if (remainder <= 0) return v;
      remainder -= 1;
      return v + 1;
    });
  }, [isMultiShot, shots, totalShotDuration, cost]);

  useEffect(() => {
    if (vidModel === 'wan-2.7') setDur('10s');
    else if (['kling-2.1', 'kling-2.6'].includes(vidModel)) setDur('5s');
    else if (['luma-ray-2', 'hailuo-v2', 'kling-3', 'wan-2.1'].includes(vidModel)) setDur('5s');
    else if (vidModel?.startsWith('seedance-2')) {
      setDur('5s');
      if (vidModel === 'seedance-2-fast' && resolution === '1080p') {
        setRes('720p');
      }
    }
  }, [vidModel, resolution]);

  const recentVideos = items
    .filter(i => i.type === 'video')
    .sort((a, b) => {
      const ta = a.createdAt?.seconds || a.createdAt?._seconds || 0;
      const tb = b.createdAt?.seconds || b.createdAt?._seconds || 0;
      return tb - ta;
    })
    .slice(0, 8);

  const localJobs = pendingJobs.filter(j => j.type === 'video');

  const handleDeleteItem = async (id) => {
    const isConfirmed = await confirmAlert({
      title: 'Delete Creation?',
      message: 'Are you sure you want to delete this creation? This action cannot be undone.'
    });
    if (!isConfirmed) return;
    try { await api.deleteGeneration(token, id); removeItem(id); toast.success('Deleted'); }
    catch { toast.error('Failed to delete'); }
  };

  const startCD = (s) => {
    setCd(s);
    if (cdRef.current) clearInterval(cdRef.current);
    cdRef.current = setInterval(() => {
      setCd(p => { if (p <= 1) { clearInterval(cdRef.current); return 0; } return p - 1; });
    }, 1000);
  };

  // Multi-shot helpers
  const addShot = () => {
    if (shots.length < 5 && remainingDuration >= 2) {
      const newDur = Math.min(5, remainingDuration);
      setShots([...shots, { prompt: '', duration: newDur }]);
    }
  };
  const removeShot = (i) => {
    if (shots.length > 1) setShots(shots.filter((_, j) => j !== i));
  };
  const updateShot = (i, field, val) => {
    const s = [...shots]; s[i] = { ...s[i], [field]: val }; setShots(s);
  };
  const updateShotDuration = (i, newDur) => {
    const s = [...shots];
    const otherTotal = s.reduce((sum, sh, j) => j === i ? sum : sum + sh.duration, 0);
    const maxForThis = MAX_TOTAL_DURATION - otherTotal;
    s[i] = { ...s[i], duration: Math.min(newDur, maxForThis) };
    setShots(s);
  };

  const handleGen = useCallback(async () => {
    if (genLockRef.current) return;
    if (!user) { if (onShowAuth) onShowAuth(); return; }
    if (cd > 0) { toast.error(`Wait ${cd}s`); return; }
    if (!isMultiShot && !prompt.trim() && !startFrame) { toast.error('Enter a prompt or upload a frame'); return; }
    if (isMultiShot && shots.every(s => !s.prompt.trim())) { toast.error('Add prompts to your shots'); return; }
    
    if (vidModel.startsWith('seedance-2') && !isFrame2Video) {
      const hasAudio = seedanceRefs.some(r => r.fileType === 'audio');
      const hasVisual = seedanceRefs.some(r => r.fileType === 'image' || r.fileType === 'video');
      if (hasAudio && !hasVisual) {
        toast.error('If audio is provided, at least one reference image or video is required.');
        return;
      }
    }

    if (credits < cost) { toast.error(`Need ${cost} credits`); navigate('/subscription'); return; }
    
    if (isModelLocked(vidModel)) {
      navigate('/subscription');
      return;
    }
    genLockRef.current = true;

    // Dynamic cooldown: 15s per video (max 4 per minute)
    const cdTime = 15 * Math.min(numVideos, 4);

    // Create pending job placeholders (non-blocking)
    const nv = Math.min(numVideos, 4);
    const jobIds = Array.from({ length: nv }, (_, i) => `vpend_${Date.now()}_${i}`);
    // Attach start frame thumbnail if available for richer pending cards
    const thumbUrl = startFrame ? (startFrame.fileUrl || (startFrame instanceof Blob || startFrame instanceof File ? URL.createObjectURL(startFrame) : null)) : null;
    const newJobs = jobIds.map(id => ({
      id, prompt: (isMultiShot ? shots[0]?.prompt : prompt || '').substring(0, 40),
      model: curModel?.label || vidModel, type: 'video', thumb: thumbUrl,
    }));
    setPendingJobs(prev => [...newJobs, ...prev]);
    setLoading(true);
    startCD(cdTime);

    // Fire generation in background
    (async () => {
      try {
        // ── Seedance 2 & Fast: uses dedicated job-queue endpoint ──
        if (vidModel?.startsWith('seedance-2')) {
          // Separate refs by type for Seedance
          const refImgFiles  = seedanceRefs.filter(r => r.fileType === 'image').map(r => r.file || r).filter(f => f instanceof File || f instanceof Blob);
          const refVidFiles  = seedanceRefs.filter(r => r.fileType === 'video').map(r => r.file || r).filter(f => f instanceof File || f instanceof Blob);
          const refAudFiles  = seedanceRefs.filter(r => r.fileType === 'audio').map(r => r.file || r).filter(f => f instanceof File || f instanceof Blob);
          const refImgUrls   = seedanceRefs.filter(r => r.fileType === 'image' && r.fileUrl && !(r.file instanceof File)).map(r => r.fileUrl);
          const refVidUrls   = seedanceRefs.filter(r => r.fileType === 'video' && r.fileUrl && !(r.file instanceof File)).map(r => r.fileUrl);
          const refAudUrls   = seedanceRefs.filter(r => r.fileType === 'audio' && r.fileUrl && !(r.file instanceof File)).map(r => r.fileUrl);

          const durInt = parseInt((duration || '5s').replace('s', '')) || 5;

          const submitRes = await api.generateSeedanceVideo(token, {
            model: vidModel,
            prompt,
            resolution,
            aspectRatio,
            duration: durInt,
            generateAudio: effectiveAudioOn,
            nsfwChecker,
            firstFrame: startFrame || null,
            lastFrame: endFrame || null,
            referenceImages: refImgFiles,
            referenceVideos: refVidFiles,
            referenceAudios: refAudFiles,
            referenceImageUrls: refImgUrls,
            referenceVideoUrls: refVidUrls,
            referenceAudioUrls: refAudUrls,
          });

          if (!submitRes.jobId) throw new Error('No jobId returned from server');

          // Update credits immediately based on deduction
          if (submitRes.creditsRemaining !== undefined) setCredits(submitRes.creditsRemaining);
          const modelName = vidModel === 'seedance-2' ? 'Seedance 2' : 'Seedance 2 Fast';
          toast(`⏳ ${modelName} queued — generating your video…`, { duration: 4000 });

          // Poll the job until done
          const completedJob = await api.pollJob(token, submitRes.jobId, null, 1200000);

          setPendingJobs(prev => prev.filter(j => !jobIds.includes(j.id)));

          const resultData = completedJob.resultData || {};
          const videoUrl = completedJob.resultUrl || resultData.videoUrl;
          if (videoUrl) {
            setGenVids([videoUrl]);
            toast.success(`${modelName} done in ${resultData.generationTime || '?'}s! 🎉`);
          }
          if (completedJob.creditsRemaining !== undefined) setCredits(completedJob.creditsRemaining);
          refreshItems(true);
          return;
        }

        // ── All other models: existing synchronous video route ──
        const shotData = isMultiShot ? shots.map(s => ({
          prompt: s.prompt,
          duration: s.duration,
        })) : undefined;

        const res = await api.generateVideo(token, {
          prompt: isMultiShot ? shots[0]?.prompt : prompt,
          model: vidModel,
          mode: isFrame2Video ? 'frame-to-video' : 'text-to-video',
          resolution,
          duration: isMultiShot ? `${totalShotDuration}s` : duration,
          aspectRatio: ['kling-2.1', 'kling-2.6', 'kling-3', 'wan-2.7'].includes(vidModel) ? aspectRatio : undefined,
          videoMode: vidModel === 'kling-2.1' && isFrame2Video ? vMode : undefined,
          promptExpansion: vidModel === 'wan-2.7' ? promptExpansion : undefined,
          numVideos, audioOn: effectiveAudioOn,
          shots: shotData,
          multiShot: isMultiShot,
          startFrame,
          endFrame,
          nsfwChecker,
          elementImages: vidModel === 'kling-3' && refImages.length > 0 ? refImages.map(r => r.fileUrl || r) : undefined,
        });
        // Remove pending jobs
        setPendingJobs(prev => prev.filter(j => !jobIds.includes(j.id)));
        if (res.success) {
          const urls = (res.videos || []).map(v => v.url).filter(Boolean);
          if (urls.length > 0) {
            setGenVids(urls);
            toast.success(`${urls.length} video${urls.length > 1 ? 's' : ''} generated in ${res.generationTime}s! 🎉`);
          }
          setCredits(res.creditsRemaining);
          refreshItems(true);
        }
      } catch (e) {
        setPendingJobs(prev => prev.filter(j => !jobIds.includes(j.id)));
        const msg = e.response?.data?.error || e.message;
        const st = e.response?.status;
        if (st === 429) { toast.error(msg || 'Rate limited'); startCD(20); }
        else if (st === 402) { toast.error('Not enough credits'); navigate('/subscription'); }
        else if (st === 403) { toast.error(msg || 'Upgrade required'); navigate('/subscription'); }
        else toast.error(msg || 'Video generation failed. Credits refunded.');
      } finally {
        genLockRef.current = false;
        setLoading(false);
      }
    })();
  }, [prompt, vidModel, resolution, duration, numVideos, audioOn, effectiveAudioOn, aspectRatio, vMode, shots, isMultiShot, totalShotDuration, startFrame, endFrame, cost, credits, cd, plan, token, user, isFrame2Video, navigate, setCredits, refreshItems, onShowAuth, curModel, seedanceRefs, promptExpansion, refImages]);


  return (
    <>
      {/* Model Picker Popup */}
      {showModelPicker && (
        <div className="mp-overlay" onClick={() => setShowModelPicker(false)}>
          <div className="mp-popup" onClick={e => e.stopPropagation()}>
            <div className="mp-header">
              <div className="mp-title">Select Video Model</div>
              <button className="mc-btn" onClick={() => setShowModelPicker(false)}><Icons.X sz={14} /></button>
            </div>
            <div className="mp-body">
              {VID_MODELS.map(m => {
                const locked = isModelLocked(m.id);
                return (
                <button key={m.id} className={`mp-item ${vidModel === m.id ? 'on' : ''}`}
                  onClick={() => handleModelChange(m.id)}>
                  {m.icon && Icons[m.icon] ? (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width: 20, height: 20, marginRight: 10, color: m.color }}>
                      {React.createElement(Icons[m.icon], { sz: 18, color: m.color })}
                    </div>
                  ) : <div className="mp-dot" style={{ background: m.color }} />}
                  <div className="mp-info">
                    <div className="mp-name">
                      {m.label}
                    </div>
                  </div>
                  <div className="mp-badges">
                    {locked && <span className="mp-badge" style={{background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)'}}>Premium</span>}
                    {m.multiShot && !locked && <span className="mp-badge pop">Multi-shot</span>}
                  </div>
                  {locked ? (
                    <div className="mp-lock">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                    </div>
                  ) : vidModel === m.id && (
                    <div className="mp-check">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                  )}
                </button>
              )})}
            </div>
          </div>
        </div>
      )}

      {/* Mobile FAB */}
      <button className={`mobile-fab ${showMobilePreview ? 'on' : ''}`} onClick={() => setShowMobilePreview(!showMobilePreview)}>
        <Icons.FilmStrip sz={22} />
        <span>{showMobilePreview ? 'Close' : 'Preview'}</span>
      </button>

      <section className="panel">
        <div className="phead"><Icons.Vid sz={16} /> {isFrame2Video ? 'Frame to Video' : 'Text to Video'}</div>

        {/* Model Selection */}
        <div className="psec">
          <div className="slbl">Model</div>
          <button className="mp-trigger" onClick={() => setShowModelPicker(true)}>
            <div className="mp-trigger-left">
              {curModel?.icon && Icons[curModel.icon] ? (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', width: 22, height: 22, marginRight: 10, color: curModel.color }}>
                  {React.createElement(Icons[curModel.icon], { sz: 20, color: curModel.color })}
                </div>
              ) : <div className="mp-dot" style={{ background: curModel?.color }} />}
              <div>
                <div className="mp-trigger-name">{curModel?.label}</div>
              </div>
            </div>
            <div className="mp-trigger-right">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
            </div>
          </button>
        </div>

        {/* Multi-shot Toggle (Kling 3 only) */}
        {canMultiShot && (
          <div className="psec">
            <div className="trow">
              <span className="tlbl">🎬 Multi-Shot Mode</span>
              <div className={`tog ${multiShotOn ? 'on' : ''}`} onClick={() => {
                setMultiShotOn(!multiShotOn);
                if (!multiShotOn) { setShots([{ prompt: '', duration: 5 }]); setEndFrame(null); }
              }}><div className="togk" /></div>
            </div>
            <div style={{ fontSize: 10, color: 'var(--t3)' }}>
              {multiShotOn ? `Multi-shot enabled · ${MAX_TOTAL_DURATION}s max total` : 'Single shot mode'}
            </div>
          </div>
        )}

        {/* Frame-to-video keyframes — ABOVE prompt */}
        {(isFrame2Video || vidModel?.startsWith('seedance-2')) && (
          <div className="psec">
            <div className="slbl">Keyframes</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
              <div className={`uzone ${startFrame ? 'on' : ''}`} onClick={() => setLibTarget('startFrame')} style={{ padding: 14, position: 'relative' }}
                onDragOver={e => e.preventDefault()}
                onDrop={async e => {
                  e.preventDefault();
                  if (e.dataTransfer.files[0]) setStartFrame(await compressImage(e.dataTransfer.files[0]));
                }}>
                {startFrame ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={startFrame.fileUrl || (startFrame instanceof Blob || startFrame instanceof File ? URL.createObjectURL(startFrame) : '')} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />
                    <button onClick={(e) => { e.stopPropagation(); setStartFrame(null); setEndFrame(null); }} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.9)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                  </div>
                ) : (
                  <><div style={{ opacity: .4 }}><Icons.Upload sz={13} /></div>Start Frame</>
                )}
              </div>
              <div className={`uzone ${endFrame ? 'on' : ''}`} onClick={() => { if(startFrame && !isMultiShot) setLibTarget('endFrame'); }} style={{ padding: 14, opacity: startFrame && !isMultiShot ? 1 : 0.3, cursor: startFrame && !isMultiShot ? 'pointer' : 'not-allowed', position: 'relative' }}
                onDragOver={e => { if (startFrame && !isMultiShot) e.preventDefault(); }}
                onDrop={async e => {
                  if (startFrame && !isMultiShot) {
                    e.preventDefault();
                    if (e.dataTransfer.files[0]) setEndFrame(await compressImage(e.dataTransfer.files[0]));
                  }
                }}>
                {endFrame ? (
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={endFrame.fileUrl || (endFrame instanceof Blob || endFrame instanceof File ? URL.createObjectURL(endFrame) : '')} alt="" style={{ width: 48, height: 48, borderRadius: 8, objectFit: 'cover' }} />
                    <button onClick={(e) => { e.stopPropagation(); setEndFrame(null); }} style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'rgba(239, 68, 68, 0.9)', border: 'none', color: '#fff', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                  </div>
                ) : (
                  <><div style={{ opacity: .4 }}><Icons.Upload sz={13} /></div>{isMultiShot ? 'N/A (Multi-Shot)' : 'End Frame (opt)'}</>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Seedance Text-to-Video Reference Upload */}
        {!isFrame2Video && vidModel.startsWith('seedance-2') && (
          <div className="psec" style={{ paddingBottom: 0, borderBottom: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span className="slbl" style={{ margin: 0 }}>Visual References</span>
              <span style={{ fontSize: 10, color: 'var(--t3)' }}>
                Optional · {seedanceRefs.length}/12
              </span>
            </div>
            
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {seedanceRefs.map((refObj, i) => (
                <div key={i} style={{ position: 'relative', width: 44, height: 44, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(139,92,246,.4)' }}>
                  {refObj.fileType === 'image' ? (
                    <img src={refObj.fileUrl || (refObj.file && (refObj.file instanceof Blob || refObj.file instanceof File) ? URL.createObjectURL(refObj.file) : '')} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                  ) : refObj.fileType === 'video' ? (
                    <video src={refObj.fileUrl || (refObj.file && (refObj.file instanceof Blob || refObj.file instanceof File) ? URL.createObjectURL(refObj.file) : '')} style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icons.Audio sz={20} /></div>
                  )}
                  <div style={{ position: 'absolute', bottom: 0, width: '100%', background: 'rgba(0,0,0,.7)', textAlign: 'center', fontSize: 8, fontWeight: 700, color: '#a78bfa' }}>
                    {refObj.tag}
                  </div>
                  <button onClick={() => {
                     setSeedanceRefs(prev => {
                       const next = prev.filter((_, j) => j !== i);
                       let imgIdx = 1, vidIdx = 1, audIdx = 1;
                       return next.map(o => {
                         if (o.fileType === 'image') return { ...o, tag: `@Image${imgIdx++}` };
                         if (o.fileType === 'video') return { ...o, tag: `@Video${vidIdx++}` };
                         if (o.fileType === 'audio') return { ...o, tag: `@Audio${audIdx++}` };
                         return o;
                       });
                     });
                  }} style={{ position: 'absolute', top: 1, right: 1, width: 14, height: 14, borderRadius: '50%', background: 'rgba(0,0,0,.7)', border: 'none', color: '#fff', fontSize: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                </div>
              ))}
              {seedanceRefs.length < 12 && (
                <button onClick={() => setLibTarget('seedanceRefs')} style={{
                  width: 44, height: 44, borderRadius: 8, border: '1px dashed rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.03)', color: 'var(--t2)', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2
                }}>
                  <Icons.Plus sz={12} />
                </button>
              )}
            </div>
            <div style={{ fontSize: 10, color: 'var(--t3)' }}>
               Supported: Images (up to 9), Videos (up to 3), Audio (up to 3). Max 12 total.
            </div>
            <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 8, fontStyle: 'italic', marginBottom: 16 }}>
              Type <span style={{color: '#a78bfa', fontWeight: 'bold'}}>@</span> in your prompt to refer to uploaded media.
            </div>
          </div>
        )}

        <MediaLibraryModal
          open={!!libTarget}
          onClose={() => setLibTarget(null)}
          allowedTypes={libTarget === 'seedanceRefs' ? ['image', 'video', 'audio'] : ['image']}
          maxSelect={libTarget === 'seedanceRefs' ? 12 : libTarget === 'refImages' ? 3 : 1}
          maxTypeCounts={libTarget === 'seedanceRefs' ? { image: 9, video: 3, audio: 3 } : null}
          onSelect={(assets) => {
            if (libTarget === 'startFrame') {
              setStartFrame(assets[0]);
            } else if (libTarget === 'endFrame') {
              setEndFrame(assets[0]);
            } else if (libTarget === 'refImages') {
              setRefImages(prev => [...prev, ...assets].slice(0, 3));
            } else if (libTarget === 'seedanceRefs') {
              setSeedanceRefs(prev => {
                let combined = [...prev, ...assets.filter(a => !prev.some(p => p.id === a.id))].slice(0, 12);
                let imgIdx = 1, vidIdx = 1, audIdx = 1;
                return combined.map(o => {
                  if (o.fileType === 'image') return { ...o, tag: `@Image${imgIdx++}` };
                  if (o.fileType === 'video') return { ...o, tag: `@Video${vidIdx++}` };
                  if (o.fileType === 'audio') return { ...o, tag: `@Audio${audIdx++}` };
                  return o;
                });
              });
            }
          }}
        />

        {/* Element References — Kling 3.0 only (now only for frame-to-video or removed, adjusting based on condition) */}
        {vidModel === 'kling-3' && isFrame2Video && (
          <div className="psec" style={{ paddingBottom: 0, borderBottom: 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span className="slbl" style={{ margin: 0 }}>Visual References</span>
              <span style={{ fontSize: 10, color: 'var(--t3)' }}>
                Optional · {refImages.length}/3
              </span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {refImages.map((img, i) => (
                <div key={i} style={{
                  position: 'relative', width: 44, height: 44, borderRadius: 8,
                  overflow: 'hidden', border: '1px solid rgba(139,92,246,.4)',
                }}>
                  <img src={img.fileUrl || (img instanceof Blob || img instanceof File ? URL.createObjectURL(img) : '')} alt="" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                  <div style={{
                    position: 'absolute', bottom: 0, width: '100%', background: 'rgba(0,0,0,.7)',
                    textAlign: 'center', fontSize: 9, fontWeight: 700, color: '#a78bfa'
                  }}>@{i + 1}</div>
                  <button onClick={() => setRefImages(prev => prev.filter((_, j) => j !== i))} style={{
                    position: 'absolute', top: 1, right: 1, width: 14, height: 14, borderRadius: '50%',
                    background: 'rgba(0,0,0,.7)', border: 'none', color: '#fff', fontSize: 9, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1
                  }}>✕</button>
                </div>
              ))}
              {refImages.length < 3 && (
                <button onClick={() => setLibTarget('refImages')} style={{
                  width: 44, height: 44, borderRadius: 8, border: '1px dashed rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.03)', color: 'var(--t2)', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2
                }}>
                  <Icons.Plus sz={12} />
                </button>
              )}
            </div>
            <div style={{ fontSize: 10, color: 'var(--t3)', marginTop: 8, fontStyle: 'italic' }}>
              Type <span style={{color: '#a78bfa', fontWeight: 'bold'}}>@</span> in your prompt to refer to uploaded images.
            </div>
          </div>
        )}

        {/* Multi-shot Shots */}
        {isMultiShot && (
          <div className="psec">
            {/* ── Timeline header ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="slbl" style={{ margin: 0 }}>Shots</span>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                  background: remainingDuration <= 0 ? 'rgba(239,68,68,0.12)' : remainingDuration < 4 ? 'rgba(245,158,11,0.12)' : 'rgba(139,92,246,0.1)',
                  color: remainingDuration <= 0 ? '#ef4444' : remainingDuration < 4 ? '#f59e0b' : 'var(--ac3)',
                  border: `1px solid ${remainingDuration <= 0 ? 'rgba(239,68,68,0.3)' : remainingDuration < 4 ? 'rgba(245,158,11,0.3)' : 'rgba(139,92,246,0.25)'}`,
                  transition: 'all 0.3s',
                }}>
                  {totalShotDuration}s / {MAX_TOTAL_DURATION}s
                </span>
              </div>
              <span style={{ fontSize: 10, color: 'var(--ac3)', fontWeight: 700 }}>
                {cost} cr total
              </span>
            </div>

            {/* ── Global timeline bar ── */}
            <div style={{
              height: 5, borderRadius: 99, marginBottom: 12,
              background: 'rgba(255,255,255,0.06)',
              overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', borderRadius: 99,
                width: `${(totalShotDuration / MAX_TOTAL_DURATION) * 100}%`,
                background: remainingDuration <= 0
                  ? 'linear-gradient(90deg, #ef4444, #dc2626)'
                  : remainingDuration < 4
                  ? 'linear-gradient(90deg, #f59e0b, #ef4444)'
                  : 'linear-gradient(90deg, #7c3aed, #a78bfa)',
                boxShadow: remainingDuration <= 0 ? '0 0 10px rgba(239,68,68,0.6)' : '0 0 8px rgba(139,92,246,0.45)',
                transition: 'width 0.2s ease, background 0.3s, box-shadow 0.3s',
              }} />
            </div>

            {/* ── Shot cards ── */}
            {shots.map((s, i) => {
              const otherTotal = shots.reduce((sum, sh, j) => j === i ? sum : sum + sh.duration, 0);
              const maxForThis = Math.max(2, MAX_TOTAL_DURATION - otherTotal);
              // remainingPct drives the colour — based on how close this shot is to its personal max
              const remPct = ((maxForThis - s.duration) / MAX_TOTAL_DURATION) * 100;
              return (
                <div key={i} className="shot-card">
                  {/* ── Card header row ── */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <div style={{
                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                        background: 'linear-gradient(135deg, #7c3aed, #a78bfa)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9, fontWeight: 900, color: '#fff',
                      }}>{i + 1}</div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--t2)' }}>Shot {i + 1}</span>
                    </div>
                    <span style={{
                      fontSize: 10, fontWeight: 800, color: 'var(--ac3)',
                      background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.22)',
                      padding: '2px 7px', borderRadius: 99, marginLeft: 'auto',
                    }}>
                      {shotCreditBreakdown[i] || 0} cr
                    </span>
                    {shots.length > 1 && (
                      <button onClick={() => removeShot(i)} style={{
                        background: 'none', border: 'none', color: 'var(--rd)',
                        cursor: 'pointer', fontSize: 10, fontWeight: 700,
                        padding: '2px 6px', borderRadius: 6,
                        transition: 'background 0.2s',
                      }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.1)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >✕ Remove</button>
                    )}
                  </div>

                  {/* ── Prompt textarea ── */}
                  <div style={{ position: 'relative', marginBottom: 8 }}>
                    <textarea className="shot-prompt" placeholder={`Hint: [Subject] + [Action/Context] + [Environment]... (Shot ${i + 1})`}
                      value={s.prompt} onChange={e => handlePromptInput(e, i)} maxLength={10000} style={{ minHeight: '120px', fontSize: '13px', resize: 'none', overflowY: 'hidden', flex: 'none', display: 'block' }} />
                    {showSuggest && activeInputIdx === i && (
                      <div style={{
                        position: 'absolute', bottom: 'calc(100% + 4px)', left: 0,
                        background: '#1e1e2d', border: '1px solid rgba(139,92,246,0.4)',
                        borderRadius: 8, padding: 6, zIndex: 10,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.6)', display: 'flex', gap: 6,
                      }}>
                        {refImages.map((img, idx) => (
                          <div key={idx} onClick={() => insertSuggestion(idx)} style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px',
                            cursor: 'pointer', borderRadius: 6, background: 'rgba(139,92,246,0.1)',
                          }}>
                            <img src={img.fileUrl || (img instanceof Blob || img instanceof File ? URL.createObjectURL(img) : '')} alt="" style={{width: 24, height: 24, borderRadius: 4, objectFit: 'cover'}} />
                            <span style={{fontSize: 11, color: '#a78bfa', fontWeight: 600}}>@image{idx+1}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <button 
                      onClick={() => handleOptimize(i)} 
                      disabled={!s.prompt.trim()}
                      style={{ 
                        position: 'absolute', bottom: 12, right: 12,
                        background: 'rgba(139,92,246,0.1)',
                        border: '1px solid rgba(139,92,246,0.2)', color: '#fff', fontSize: 10, 
                        padding: '4px 8px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4, 
                        opacity: !s.prompt.trim() ? 0.5 : 1, cursor: 'pointer', zIndex: 5
                      }}
                    >
                      <Icons.Sparkle sz={10} /> Optimize
                    </button>
                  </div>

                  {/* ── Compact duration slider ──
                      IMPORTANT: max is always MAX_TOTAL_DURATION (fixed) so other sliders
                      never jump position. We clamp the actual value in onChange instead. ── */}
                  <FuturisticSlider
                    label="Dur"
                    min={2}
                    max={MAX_TOTAL_DURATION}
                    step={1}
                    value={s.duration}
                    onChange={val => updateShotDuration(i, Math.min(val, maxForThis))}
                    onReset={() => updateShotDuration(i, 2)}
                    remainingPct={remPct}
                    warn={s.duration >= maxForThis}
                  />
                </div>
              );
            })}

            {/* ── Add shot / No time left ── */}
            {shots.length < 5 && remainingDuration >= 2 && (
              <button className="add-shot-btn" onClick={addShot}>
                <Icons.Plus sz={13} /> Add Shot
                <span style={{ marginLeft: 4, opacity: 0.6, fontSize: 10 }}>· {remainingDuration}s left</span>
              </button>
            )}
            {remainingDuration < 2 && shots.length < 5 && (
              <div style={{
                fontSize: 10, textAlign: 'center', marginTop: 6, padding: '6px 12px',
                borderRadius: 8, background: 'rgba(239,68,68,0.08)',
                color: '#ef4444', border: '1px solid rgba(239,68,68,0.2)',
              }}>
                ⏱ No time left — reduce a shot duration to add another
              </div>
            )}
          </div>
        )}

        {/* Prompt (when not multi-shot) */}
        {!isMultiShot && (
          <div className="psec">
            <div className="slbl">Prompt</div>
            <div className="pwrap" style={{ position: 'relative' }}>
              <textarea className="ptxt"
                placeholder="Hint: [Subject] + [Action/Context] + [Environment/Background] + [Lighting/Mood] + [Art Style/Camera Settings] + [Technical Quality Tags]"
                value={prompt} onChange={e => handlePromptInput(e, null)} maxLength={10000} style={{ minHeight: '120px', fontSize: '13px', resize: 'none', overflowY: 'hidden', flex: 'none', display: 'block' }} />
              {showSuggest && activeInputIdx === null && (
                <div style={{
                  position: 'absolute', bottom: 'calc(100% + 4px)', left: 0, background: '#1e1e2d',
                  border: '1px solid rgba(139,92,246,0.5)', borderRadius: 8, padding: 6, zIndex: 100,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.6)', display: 'flex', gap: 6, overflowX: 'auto', maxWidth: '100%'
                }}>
                  {vidModel === 'kling-3' && refImages.map((img, idx) => (
                    <div key={idx} onClick={() => insertSuggestion(idx)} style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', flexShrink: 0,
                      cursor: 'pointer', borderRadius: 6, background: 'rgba(139,92,246,0.15)',
                      border: '1px solid rgba(139,92,246,0.2)', transition: 'background 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,92,246,0.3)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(139,92,246,0.15)'}>
                      <img src={img.fileUrl || (img instanceof Blob || img instanceof File ? URL.createObjectURL(img) : '')} alt="" style={{width: 24, height: 24, borderRadius: 4, objectFit: 'cover'}} />
                      <span style={{fontSize: 12, color: '#a78bfa', fontWeight: 600}}>@image{idx+1}</span>
                    </div>
                  ))}
                  {vidModel.startsWith('seedance-2') && seedanceRefs.map((ref, idx) => (
                    <div key={`seed-${idx}`} onClick={() => insertSuggestion(ref.tag)} style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', flexShrink: 0,
                      cursor: 'pointer', borderRadius: 6, background: 'rgba(139,92,246,0.15)',
                      border: '1px solid rgba(139,92,246,0.2)', transition: 'background 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(139,92,246,0.3)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(139,92,246,0.15)'}>
                      {ref.fileType === 'image' ? (
                        <img src={ref.fileUrl} alt="" style={{width: 24, height: 24, borderRadius: 4, objectFit: 'cover'}} />
                      ) : ref.fileType === 'video' ? (
                        <video src={ref.fileUrl} style={{width: 24, height: 24, borderRadius: 4, objectFit: 'cover'}} />
                      ) : (
                        <div style={{ width: 24, height: 24, borderRadius: 4, background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icons.Audio sz={14} /></div>
                      )}
                      <span style={{fontSize: 12, color: '#a78bfa', fontWeight: 600}}>{ref.tag}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="pbot">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span className="pcnt" style={{ fontSize: '10px' }}>{prompt.length}/10000</span>
                  {prompt.length > 0 && (
                    <button 
                      onClick={() => setPrompt('')} 
                      style={{ background: 'transparent', border: 'none', color: 'var(--rd)', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' }}
                      title="Clear prompt"
                    >
                      <Icons.X sz={14} />
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button 
                    className="tbtn" 
                    onClick={() => setShowTemplates(true)} 
                    style={{
                      background: 'rgba(245,158,11,0.1)',
                      border: '1px solid rgba(245,158,11,0.2)', color: '#f59e0b', fontSize: 10,
                      padding: '4px 8px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4,
                      cursor: 'pointer', zIndex: 5
                    }}
                  >
                    <Icons.Sparkle sz={10} /> Templates
                  </button>
                  <button 
                    className="tbtn" 
                    onClick={() => handleOptimize(null)} 
                    disabled={!prompt.trim()}
                    style={{
                      background: 'rgba(139,92,246,0.1)',
                      border: '1px solid rgba(139,92,246,0.2)', color: '#fff', fontSize: 10,
                      padding: '4px 8px', borderRadius: 6, display: 'flex', alignItems: 'center', gap: 4,
                      opacity: !prompt.trim() ? 0.5 : 1, cursor: 'pointer', zIndex: 5
                    }}
                  >
                    <><Icons.Sparkle sz={10} /> Optimize</>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Video Settings */}
        <div className="psec">
          {vidModel !== 'wan-2.7' && <div className="slbl">Video Settings</div>}

          {vidModel !== 'kling-2.1' && vidModel !== 'kling-3' && vidModel !== 'wan-2.7' && vidModel !== 'kling-2.6' && !vidModel.startsWith('seedance-2') && (
            <>
              {/* Audio Toggle */}
              <div className="trow">
                <span className="tlbl"><Icons.Audio sz={13} /> Audio</span>
                <div className={`tog ${audioOn ? 'on' : ''}`} onClick={() => setAudioOn(!audioOn)}><div className="togk" /></div>
              </div>
              <div style={{ fontSize: 10, color: 'var(--t3)', marginBottom: 12 }}>
                Audio {audioOn ? 'enabled (+30% cost)' : 'disabled'}
              </div>

              {/* Resolution */}
              <div className="slbl" style={{ marginTop: 4 }}>Resolution</div>
              <div className="pills" style={{ marginBottom: 12 }}>
                {RESOLUTIONS.map(r => (
                  <button key={r} className={`pill ${resolution === r ? 'on' : ''}`} onClick={() => setRes(r)}>
                    {r} {r === '1080p' && '(+50%)'}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* Seedance 2 & Fast */}
          {vidModel?.startsWith('seedance-2') && (
            <>
              {/* Safe Mode Toggle */}
              <div className="trow">
                <span className="tlbl">Safe Mode (NSFW Filter)</span>
                <div className={`tog ${nsfwChecker ? 'on' : ''}`} onClick={() => setNsfwChecker(!nsfwChecker)}><div className="togk" /></div>
              </div>
              <div style={{ fontSize: 10, color: 'var(--t3)', marginBottom: 12 }}>
                {nsfwChecker ? 'Content filter enabled' : 'Content filter disabled (NSFW allowed)'}
              </div>

              {/* Audio Toggle */}
              <div className="trow">
                <span className="tlbl"><Icons.Audio sz={13} /> Audio</span>
                <div className={`tog ${audioOn ? 'on' : ''}`} onClick={() => setAudioOn(!audioOn)}><div className="togk" /></div>
              </div>
              <div style={{ fontSize: 10, color: 'var(--t3)', marginBottom: 12 }}>
                Audio {audioOn ? 'enabled (included in price)' : 'disabled'}
              </div>

              {/* Aspect Ratio */}
              {!isFrame2Video && (
                <>
                  <div className="slbl" style={{ marginTop: 12 }}>Aspect Ratio</div>
                  <div style={{ marginBottom: 12 }}>
                    <AspectRatioSelector
                      options={['16:9', '4:3', '1:1', '3:4', '9:16', '21:9']}
                      value={aspectRatio}
                      onChange={setAspectRatio}
                    />
                  </div>
                </>
              )}

              {/* Resolution — per/sec pricing */}
              <div className="slbl">Resolution</div>
              <div className="pills" style={{ marginBottom: 4 }}>
                {(vidModel === 'seedance-2' ? ['480p', '720p', '1080p'] : ['480p', '720p']).map(r => (
                  <button key={r} className={`pill ${resolution === r ? 'on' : ''}`} onClick={() => setRes(r)}>
                    {r} {r === '480p' ? '(35cr/s)' : r === '720p' ? '(70cr/s)' : '(140cr/s)'}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 10, color: 'var(--t3)', marginBottom: 12 }}>
                With video reference: 480p=25cr/s · 720p=45cr/s {vidModel === 'seedance-2' && '· 1080p=90cr/s'}
              </div>
            </>
          )}

          {/* Kling 3.0 — Pro/Standard + Audio + Aspect Ratio */}
          {vidModel === 'kling-3' && (
            <>
              {/* Sound Toggle */}
              <div className="trow">
                <span className="tlbl"><Icons.Audio sz={13} /> Sound</span>
                <div className={`tog ${audioOn ? 'on' : ''}`} onClick={() => setAudioOn(!audioOn)}><div className="togk" /></div>
              </div>
              <div style={{ fontSize: 10, color: 'var(--t3)', marginBottom: 12 }}>
                {`Sound ${audioOn ? 'enabled' : 'disabled'}`}
              </div>

              {/* Quality Mode: Standard / Pro */}
              <div className="slbl" style={{ marginTop: 4 }}>Quality Mode</div>
              <div className="pills" style={{ marginBottom: 4 }}>
                <button className={`pill ${resolution === '720p' ? 'on' : ''}`} onClick={() => setRes('720p')}>
                  ⚡ Standard
                </button>
                <button className={`pill ${resolution === '1080p' ? 'on' : ''}`} onClick={() => setRes('1080p')}>
                  ✨ Pro (+50%)
                </button>
              </div>
              <div style={{ fontSize: 10, color: 'var(--t3)', marginBottom: 12 }}>
                {resolution === '1080p'
                  ? `Pro · ${aspectRatio === '16:9' ? '1920×1080' : aspectRatio === '9:16' ? '1080×1920' : '1080×1080'}`
                  : `Standard · ${aspectRatio === '16:9' ? '1280×720' : aspectRatio === '9:16' ? '720×1280' : '720×720'}`}
              </div>

              {/* Aspect Ratio */}
              <div className="slbl">Aspect Ratio</div>
              <div style={{ marginBottom: 12 }}>
                <AspectRatioSelector
                  options={['9:16', '1:1', '16:9']}
                  value={aspectRatio}
                  onChange={setAspectRatio}
                />
              </div>
            </>
          )}


          {/* Kling 2.1 Settings */}
          {vidModel === 'kling-2.1' && (
            <>
              <div className="trow">
                <span className="tlbl">Safe Mode (NSFW Filter)</span>
                <div className={`tog ${nsfwChecker ? 'on' : ''}`} onClick={() => setNsfwChecker(!nsfwChecker)}><div className="togk" /></div>
              </div>
              <div style={{ fontSize: 10, color: 'var(--t3)', marginBottom: 12 }}>
                {nsfwChecker ? 'Content filter enabled' : 'Content filter disabled (NSFW allowed)'}
              </div>
            </>
          )}

          {/* Kling 2.1 Aspect Ratio (text-to-video only) */}
          {vidModel === 'kling-2.1' && !isFrame2Video && (
            <>
              <div className="slbl">Aspect Ratio</div>
              <div style={{ marginBottom: 12 }}>
                <AspectRatioSelector
                  options={['9:16', '1:1', '16:9']}
                  value={aspectRatio}
                  onChange={setAspectRatio}
                />
              </div>
            </>
          )}

          {vidModel === 'kling-2.1' && isFrame2Video && (
            <>
              <div className="slbl">Quality Mode</div>
              <div className="pills" style={{ marginBottom: 12 }}>
                {['Standard','Pro','Master'].map(m => (
                  <button key={m} className={`pill ${vMode === m ? 'on' : ''}`} onClick={() => setVMode(m)}>{m}</button>
                ))}
              </div>
            </>
          )}

          {/* Kling 2.6 — Audio + Aspect Ratio (text-to-video only) */}
          {vidModel === 'kling-2.6' && (
            <>
              {/* Audio Toggle */}
              <div className="trow">
                <span className="tlbl"><Icons.Audio sz={13} /> Audio</span>
                <div className={`tog ${audioOn ? 'on' : ''}`} onClick={() => setAudioOn(!audioOn)}><div className="togk" /></div>
              </div>

              {/* Aspect Ratio (text-to-video only) */}
              {!isFrame2Video && (
                <>
                  <div className="slbl">Aspect Ratio</div>
                  <div style={{ marginBottom: 12 }}>
                    <AspectRatioSelector
                      options={['9:16', '1:1', '16:9']}
                      value={aspectRatio}
                      onChange={setAspectRatio}
                    />
                  </div>
                </>
              )}
            </>
          )}

          {/* Wan 2.7 Settings */}
          {vidModel === 'wan-2.7' && (
            <>
              {/* Aspect Ratio (text-to-video only) */}
              {!isFrame2Video && (
                <>
                  <div className="slbl">Aspect Ratio</div>
                  <div style={{ marginBottom: 12 }}>
                    <AspectRatioSelector
                      options={['9:16', '3:4', '1:1', '4:3', '16:9']}
                      value={aspectRatio}
                      onChange={setAspectRatio}
                    />
                  </div>
                </>
              )}

              {/* Resolution */}
              <div className="slbl">Resolution</div>
              <div className="pills" style={{ marginBottom: 12 }}>
                {['720p', '1080p'].map(r => (
                  <button key={r} className={`pill ${resolution === r ? 'on' : ''}`} onClick={() => setRes(r)}>
                    {r} {r === '1080p' && '(+50%)'}
                  </button>
                ))}
              </div>

              {/* Prompt Expansion Toggle */}
              <div className="trow" style={{ marginTop: 12 }}>
                <span className="tlbl"><Icons.Sparkle sz={13} /> Prompt Expansion</span>
                <div className={`tog ${promptExpansion ? 'on' : ''}`} onClick={() => setPromptExpansion(!promptExpansion)}><div className="togk" /></div>
              </div>
              <div style={{ fontSize: 10, color: 'var(--t3)', marginBottom: 16 }}>
                {promptExpansion ? 'AI will expand your prompt for better details' : 'Use prompt exactly as written'}
              </div>
            </>
          )}

          {/* Duration — only when NOT multi-shot */}
          {!isMultiShot && (
            <>
              {['kling-3', 'wan-2.7', 'seedance-2', 'seedance-2-fast'].includes(vidModel) ? (
                <div style={{ marginBottom: 12 }}>
                  <FuturisticSlider 
                    label="Duration"
                    min={vidModel.startsWith('seedance-2') ? 4 : 3} max={vidModel === 'wan-2.7' ? 10 : 15} step={1}
                    value={parseInt(duration) || 5}
                    onChange={val => setDur(`${val}s`)}
                    onReset={() => setDur('5s')}
                  />
                </div>
              ) : (
                <>
                  <div className="slbl">Duration</div>
                  <div className="pills" style={{ marginBottom: 12 }}>
                    {(['kling-2.1', 'kling-2.6'].includes(vidModel) ? ['5s','10s'] : ['5s','6s','8s','12s']).map(d => (
                      <button key={d} className={`pill ${duration === d ? 'on' : ''}`} onClick={() => setDur(d)}>{d}</button>
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* Parallel Videos */}
          <div className="slbl">Parallel Videos</div>
          <div className="num-sel">
            <button onClick={() => setNumVideos(Math.max(1, numVideos - 1))}><Icons.Minus sz={12} /></button>
            <span className="nv">{numVideos}</span>
            <button onClick={() => setNumVideos(Math.min(4, numVideos + 1))}><Icons.Plus sz={12} /></button>
            <span style={{ fontSize: 11, color: 'var(--t3)', marginLeft: 4 }}>/ 4 max</span>
          </div>
        </div>

        {/* Generate Button */}
        <div className="gen-sticky">
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--t2)', marginBottom: 12, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap', textAlign: 'center' }}>
            <strong style={{ color: curModel.color }}>{curModel.label}</strong>
            {isMultiShot && <span>· {shots.length} shot{shots.length > 1 ? 's' : ''} · {totalShotDuration}s</span>}
            {!isMultiShot && <span>� {duration}</span>}
            {isFrame2Video && <span>· F2V</span>}
          </div>
          <div className="cost-row">
            <span className="cost-l">Total Cost</span>
            <span className="cost-v">{cost} cr → {Math.max(0, (credits || 0) - cost)} left</span>
          </div>
          <button className="gbtn" onClick={handleGen}
            disabled={loading || credits < cost || cd > 0}>
            {cd > 0 ? `Wait ${cd}s` :
              !user ? <>✨ Sign up to Generate</> :
              <><Icons.Wand sz={15} /> Generate {isMultiShot && 'Sequence'}</>}
          </button>
          {user && credits < cost && (
            <p style={{ textAlign: 'center', color: 'var(--rd)', fontSize: 11, marginTop: 6, cursor: 'pointer' }}
              onClick={() => navigate('/subscription')}>Insufficient credits — <u>upgrade</u></p>
          )}
          {cd > 0 && <p style={{ textAlign: 'center', fontSize: 10, color: 'var(--or)', marginTop: 6 }}>Cooldown: {cd}s</p>}
        </div>
      </section>

      {/* Main Canvas */}
      <div className={`main ${showMobilePreview ? 'mobile-preview-open' : ''}`}>
        <div className="mtbar">
          <button className="mtab main-back-mobile" onClick={() => setShowMobilePreview(false)}>
            <Icons.X sz={13} /> Close
          </button>
          <div className="mtab on">Preview</div>
          <div style={{ flex: 1 }} />
          <div className="mtab" onClick={() => navigate('/gallery')} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Icons.Grid sz={13} /> Creations
          </div>
        </div>
        <div className="mbody">
          {/* Preview area — show all generated videos */}
          {genVids.length > 0 && (
            <div className="prev">
              <div className="prev-wrap">
                <div className="prev-toolbar">
                  <div className="prev-tb-right" style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <button className="prev-icon-btn" onClick={() => { import('../utils/downloadFile').then(m => m.downloadFile(genVids[0], token, true)).then(() => {}).catch(() => {}); }} title="Download first">
                      <Icons.DL sz={15} />
                    </button>
                    <button className="prev-icon-btn del" onClick={async () => {
                      const isConfirmed = await confirmAlert({
                        title: 'Clear Results?',
                        message: 'Are you sure you want to clear these results?'
                      });
                      if (isConfirmed) setGenVids([]);
                    }} title="Clear">
                      <Icons.Trash sz={15} />
                    </button>
                  </div>
                </div>
                {genVids.length === 1 ? (
                  <video src={genVids[0]} controls autoPlay loop style={{ width: '100%', borderRadius: 'var(--r3)', background: '#000' }} />
                ) : (
                  <div className="ggrid" style={{ marginBottom: 12 }}>
                    {genVids.map((url, i) => (
                      <div key={i} className="gitem" onClick={() => setLbItem({ videoUrl: url, prompt, modelName: curModel?.label })}>
                        <video src={url} muted loop playsInline
                          onMouseEnter={e => e.target.play()}
                          onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <div className="gbadge" style={{ background: 'rgba(0,0,0,.6)' }}><Icons.FilmStrip sz={11} /></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {(localJobs.length > 0 || recentVideos.length > 0) && (
            <>
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--t2)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icons.Vid sz={13} /> {localJobs.length > 0 ? 'Generating...' : 'Recent Videos'}
              </div>
              <div className="ggrid">
                {/* Pending shimmer cards */}
                {localJobs.map(j => (
                  <div key={j.id} className="gitem-pending" style={{ position: 'relative' }}>
                    {j.thumb && (
                      <img src={j.thumb} alt="" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--r2)', opacity: 0.45 }} />
                    )}
                    <div className="pend-icon" style={{ position: 'relative', zIndex: 1 }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9 }}>
                        <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"></rect>
                        <line x1="7" y1="2" x2="7" y2="22"></line>
                        <line x1="17" y1="2" x2="17" y2="22"></line>
                        <line x1="2" y1="12" x2="22" y2="12"></line>
                        <line x1="2" y1="7" x2="7" y2="7"></line>
                        <line x1="2" y1="17" x2="7" y2="17"></line>
                        <line x1="17" y1="17" x2="22" y2="17"></line>
                        <line x1="17" y1="7" x2="22" y2="7"></line>
                      </svg>
                    </div>
                    <div className="pend-label" style={{ position: 'relative', zIndex: 1, marginTop: 4 }}>{j.prompt || 'Generating...'}</div>
                    <div className="pend-model" style={{ position: 'relative', zIndex: 1 }}>{j.model}</div>
                    <div className="pend-bar-wrap" style={{ position: 'relative', zIndex: 1 }}><div className="pend-bar" /></div>
                  </div>
                ))}
              </div>
              
              <div className="grouped-collections">
                {groupItemsByDate(recentVideos).map(group => (
                  <div key={group.label} className="date-group-wrap">
                    <div className="date-group-label" style={{ marginBottom: 12 }}>{group.label}</div>
                    <div className="ggrid">
                      {group.items.map(i => (
                        <div key={i.id} className="gitem" onClick={() => setLbItem(i)}>
                          {i.videoUrl ? (
                            <video src={i.videoUrl} muted loop playsInline onMouseEnter={e => e.target.play()} onMouseLeave={e => { e.target.pause(); e.target.currentTime = 0; }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <img src={i.imageUrl} alt="" />
                          )}
                          <div className="gbadge" style={{ background: 'rgba(6,182,212,.12)', color: '#06b6d4' }}><Icons.FilmStrip sz={11} /></div>
                          <div className="gact" style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 6, zIndex: 15, opacity: 0, transition: 'opacity 0.2s' }}>
                            <button onClick={(e) => { e.stopPropagation(); import('../utils/downloadMedia').then(m => m.downloadMedia(i.videoUrl || i.imageUrl, i.type, token)); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#fff', display: 'flex' }}><Icons.DL sz={14} /></button>
                            <button onClick={(e) => { e.stopPropagation(); if(typeof handleDeleteItem === 'function') handleDeleteItem(i.id || i.$id); }} style={{ background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: 6, padding: '5px', cursor: 'pointer', color: '#ef4444', display: 'flex' }}><Icons.Trash sz={14} /></button>
                          </div>
                          <div className="gov">
                            <div className="gm">
                              <div style={{ fontWeight: 700, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{i.prompt}</div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>{i.modelName || i.model}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <Lightbox item={lbItem} onClose={() => setLbItem(null)} onDelete={handleDeleteItem} />
      
      {/* ── TEMPLATES SLIDE WINDOW ── */}
      <TemplateSlideWindow
        isOpen={showTemplates}
        onClose={() => setShowTemplates(false)}
        modelId={vidModel}
        mode="video"
        onSelect={(tmpl) => {
          setShowTemplates(false);
          const isVideo = tmpl.type === 'video' || tmpl.editType === 'text_to_video' || tmpl.editType === 'frame-to-video';
          
          const refImageUrls = Array.isArray(tmpl.refImageUrl)
            ? tmpl.refImageUrl.filter(Boolean)
            : tmpl.refImageUrl ? [tmpl.refImageUrl] : [];

          const newState = {
            action: 'template-use',
            prompt: tmpl.prompt || '',
            modelId: tmpl.model || undefined,
            isMultiShot: tmpl.isMultiShot || false,
            refVideoUrl: tmpl.refVideoUrl || null,
            refAudioUrl: tmpl.refAudioUrl || null,
            endImageUrl: tmpl.endImageUrl || null,
            motionControl: tmpl.motionControl || false,
            lipsync: tmpl.lipsync || false,
          };

          if (tmpl.startImageUrl) {
            newState.sourceUrl = tmpl.startImageUrl;
            newState.action = 'image-to-video';
          } else if (refImageUrls.length > 0 || tmpl.refVideoUrl || tmpl.refAudioUrl) {
            newState.sourceUrl = refImageUrls[0] || null;
            newState.refImageUrls = refImageUrls;
            newState.action = 'use-as-reference';
          }
          
          initProcessed.current = false;
          navigate(location.pathname, { state: newState });
        }}
      />
    </>
  );
}


